import { useState, useEffect } from "react";
import Pagination from "../../../components/Pagination/Pagination";
import "./SalaryMaster.scss";
import axiosInstance from "../../../axiosInstance";
import CreatePayroll from "./CreatePayroll";
import EditPayroll from "./EditPayroll";
import { toast } from "react-toastify";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const SalaryMaster = () => {
  const [employeeData, setEmployeeData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [filterBranch, setFilterBranch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const fetchEmployeeData = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/salary-master/employees`
      );
      const data = response.data;

      const mappedData = data.map((employee) => ({
        id: employee.employeeId,
        employeeId: employee.userEmployeeId,
        name: employee.employeeName,
        departments: employee.departments.join(", ") || "N/A",
        role: employee.role || "N/A",
        branch: employee.branch || "N/A",
        branchPayroll: employee.payrollName || "N/A",
        doj: new Date(employee.joinedDate).toLocaleDateString(),
        employeePayrollId: employee.employeePayrollId,
        netSalary: Number(employee.netSalary) || 0,
        grossSalary: Number(employee.grossSalary) || 0,
        status:
          employee.payrollName && employee.payrollName !== "N/A"
            ? "assigned"
            : "not-assigned",
      }));

      setEmployeeData(mappedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  // Enhanced sorting function
  const getSortedData = (data) => {
    return [...data].sort((a, b) => {
      // First priority: Sort by status (not-assigned first, then assigned)
      if (a.status !== b.status) {
        if (a.status === "not-assigned" && b.status === "assigned") return -1;
        if (a.status === "assigned" && b.status === "not-assigned") return 1;
      }

      // Second priority: If both have same status and both are assigned, sort by net salary (highest to lowest)
      if (a.status === "assigned" && b.status === "assigned") {
        return b.netSalary - a.netSalary;
      }

      // For other cases or not-assigned employees, maintain alphabetical order by name
      return a.name.localeCompare(b.name);
    });
  };

  const filteredData = employeeData.filter((employee) => {
    const matchesSearch = [
      "name",
      "branch",
      "role",
      "departments",
      "branchPayroll",
    ].some((key) =>
      employee[key]?.toLowerCase().includes(search.toLowerCase())
    );

    const matchesBranch = filterBranch
      ? employee.branch === filterBranch
      : true;
    const matchesRole = filterRole ? employee.role === filterRole : true;
    const matchesDepartment = filterDepartment
      ? employee.departments.includes(filterDepartment)
      : true;
    const matchesStatus = filterStatus
      ? employee.status === filterStatus
      : true;

    return (
      matchesSearch &&
      matchesBranch &&
      matchesRole &&
      matchesDepartment &&
      matchesStatus
    );
  });

  const sortedData = getSortedData(filteredData);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / entriesPerPage);
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedData.slice(indexOfFirstEntry, indexOfLastEntry);

  // Statistics
  const assignedCount = employeeData.filter(
    (emp) => emp.status === "assigned"
  ).length;
  const notAssignedCount = employeeData.filter(
    (emp) => emp.status === "not-assigned"
  ).length;
  const totalEmployees = employeeData.length;

  const handleEditClick = (employee) => {
    setSelectedEmployee(employee);
    setActionType("edit");
  };

  const handleCreateClick = (employee) => {
    setSelectedEmployee(employee);
    setActionType("create");
  };

  const handleCloseModal = () => {
    setSelectedEmployee(null);
    setActionType(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const StatusCard = ({ status, employee }) => {
    return (
      <div className={`status-card status-card--${status}`}>
        <span className="status-card__text">
          {status === "assigned" ? "Updated" : "Not Updated"}
        </span>
      </div>
    );
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="salary-master">
      <div className="salary-master__header">
        <h1 className="display6">Salary Master</h1>

        {/* Statistics Cards */}
        <div className="salary-master__stats">
          <div className="stat-card stat-card--total">
            <div className="stat-card__number">{totalEmployees}</div>
            <div className="stat-card__label">Total Employees</div>
          </div>
          <div className="stat-card stat-card--assigned">
            <div className="stat-card__number">{assignedCount}</div>
            <div className="stat-card__label">Assigned Payroll</div>
          </div>
          <div className="stat-card stat-card--pending">
            <div className="stat-card__number">{notAssignedCount}</div>
            <div className="stat-card__label">Pending Assignment</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="salary-master__search">
          <div className="filters-group">
            <select
              value={filterBranch}
              onChange={(e) => setFilterBranch(e.target.value)}
              className="filter-select"
            >
              <option value="">All Branches</option>
              {[...new Set(employeeData.map((e) => e.branch))].map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="filter-select"
            >
              <option value="">All Roles</option>
              {[...new Set(employeeData.map((e) => e.role))].map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>

            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="filter-select"
            >
              <option value="">All Departments</option>
              {[
                ...new Set(
                  employeeData.flatMap((e) => e.departments.split(", "))
                ),
              ]
                .filter(Boolean)
                .map((dep) => (
                  <option key={dep} value={dep}>
                    {dep}
                  </option>
                ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="not-assigned">Not Assigned</option>
            </select>
          </div>

          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search employees, roles, departments..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <div className="search-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19S2 15.194 2 10.5 5.806 2 10.5 2 19 5.806 19 10.5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Entries dropdown below search */}
            <div className="entries-select">
              <select
                id="entriesPerPage"
                className="entries-dropdown"
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page
                }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <span className="entries-label">entries</span>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Table with Frozen Headers */}
      <div className="salary-master__table-container">
        <div className="table-wrapper">
          <table className="professional-table w-full">
            <thead className="table-header">
              <tr>
                <th className="table-header__cell table-header__cell--sno">
                  S.No
                </th>
                <th className="table-header__cell table-header__cell--employee">
                  Employee
                </th>
                <th className="table-header__cell table-header__cell--status">
                  Status
                </th>
                <th className="table-header__cell table-header__cell--role">
                  Role
                </th>
                <th className="table-header__cell table-header__cell--department">
                  Department
                </th>
                <th className="table-header__cell table-header__cell--branch">
                  Branch
                </th>
                <th className="table-header__cell table-header__cell--payroll">
                  Payroll
                </th>
                <th className="table-header__cell table-header__cell--doj">
                  DOJ
                </th>
                <th className="table-header__cell table-header__cell--salary">
                  Net Salary
                </th>
                <th className="table-header__cell table-header__cell--salary">
                  Gross Salary
                </th>
                <th className="table-header__cell table-header__cell--action">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {currentEntries.map((employee, index) => (
                <tr key={employee.id} className="table-row">
                  <td className="table-cell table-cell--sno">
                    {indexOfFirstEntry + index + 1}
                  </td>

                  {/* Employee Name + ID */}
                  <td className="table-cell table-cell--employee">
                    <div className="employee-info">
                      <div className="employee-info__avatar">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="employee-info__details">
                        <div className="employee-info__name">
                          {employee.name}
                        </div>
                        <div className="employee-info__id">
                          ID: {employee.employeeId}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Remove the separate employeeId td */}

                  <td className="table-cell table-cell--status">
                    <StatusCard status={employee.status} employee={employee} />
                  </td>
                  <td className="table-cell table-cell--role">
                    {employee.role}
                  </td>
                  <td className="table-cell table-cell--department">
                    {employee.departments}
                  </td>
                  <td className="table-cell table-cell--branch">
                    {employee.branch}
                  </td>
                  <td className="table-cell table-cell--payroll">
                    {employee.branchPayroll !== "N/A"
                      ? employee.branchPayroll
                      : "-"}
                  </td>
                  <td className="table-cell table-cell--doj">{employee.doj}</td>
                  <td className="table-cell table-cell--salary">
                    {employee.status === "assigned"
                      ? employee.netSalary?.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }) || "₹0"
                      : "-"}
                  </td>
                  <td className="table-cell table-cell--salary">
                    {employee.status === "assigned"
                      ? employee.grossSalary?.toLocaleString("en-IN", {
                          style: "currency",
                          currency: "INR",
                          maximumFractionDigits: 0,
                        }) || "₹0"
                      : "-"}
                  </td>
                  <td className="table-cell table-cell--action">
                    <div className="action-buttons">
                      {employee.status === "not-assigned" ? (
                        <button
                          className="action-btn action-btn--create"
                          onClick={() => handleCreateClick(employee)}
                          title="Assign Payroll"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 5V19M5 12H19"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          Update
                        </button>
                      ) : (
                        <button
                          className="action-btn action-btn--edit"
                          onClick={() => handleEditClick(employee)}
                          title="Edit Payroll"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M12 20H21M16.5 3.5C17.328 2.672 18.672 2.672 19.5 3.5S20.328 5.828 19.5 6.656L7 19.156 3 20L4.844 16L16.5 3.5Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination and Info */}
      <div className="salary-master__footer">
        <div className="entries-info">
          Showing {indexOfFirstEntry + 1} to{" "}
          {Math.min(indexOfLastEntry, sortedData.length)} of {sortedData.length}{" "}
          entries
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Edit Modal */}
      {actionType === "edit" && selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal-container">
            <button className="modal-close" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <EditPayroll
              employee={selectedEmployee}
              closeModal={handleCloseModal}
              showToast={showToast}
              fetchEmployeeData={fetchEmployeeData}
            />
          </div>
        </div>
      )}

      {/* Create Modal */}
      {actionType === "create" && selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal-container">
            <button className="modal-close" onClick={handleCloseModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <CreatePayroll
              employee={selectedEmployee}
              closeModal={handleCloseModal}
              fetchEmployeeData={fetchEmployeeData}
              showToast={showToast}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryMaster;
